import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output,
  SimpleChanges, TemplateRef, ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CustomTableComponent } from '../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../shared/ui/custom-table/model/table-config.model';
import { TableStateSnapshot } from '../../../../modules_v2/shared/utils/table-types';
import { mapTableState, tableStateEquals } from '../../../../modules_v2/shared/utils/table-state.utils';
import { ConfirmService } from '../../../../modules_v2/shared/services/confirm.service';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Utils } from '../../../../shared/utils';
import { Model193Service } from '../../model-193.service';
import { Model193, Model193Recipient, Model193Validation, ORIGEN_CONTABILIDAD, ORIGEN_MANUAL } from '../taxes-193.model';
import { Model193RecipientDialogComponent } from '../model-193-recipient-dialog/model-193-recipient-dialog.component';

@Component({
  selector: 'app-model-193-recipients',
  templateUrl: './model-193-recipients.component.html',
  styleUrls: ['./model-193-recipients.component.css'],
  standalone: false
})
export class Model193RecipientsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() ejercicio: number;
  @Input() declarante: Model193;
  @Input() version = 0;
  @Input() validations: Model193Validation[] = [];
  @Input() foco: { texto: string, id: number } | null = null;

  @Output() changed = new EventEmitter<void>();

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('origenTpl', { static: true }) origenTpl!: TemplateRef<any>;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;
  quickSearch = '';
  totalBase = 0;
  totalRetenciones = 0;
  totalPerceptores = 0;

  private lastState: TableStateSnapshot = {
    pageIndex: 0,
    pageSize: 25,
    filters: {},
    sortField: '',
    sortDirection: ''
  };

  private secuencia = 0;
  private abrirAlCargar = false;
  private focoAplicado = 0;
  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: Model193Service,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const dinero = (valor: any) => Utils.FormatMoney(valor) + ' €';

    this.tableConfig = {
      columns: [
        { key: 'origen', label: 'Origen', sortable: true, sortKey: 'origen', customTemplate: this.origenTpl, width: '130px' },
        { key: 'nifPerceptor', label: 'NIF del perceptor', sortable: true, sortKey: 'nif', width: '140px' },
        {
          key: 'nombre', label: 'Apellidos y nombre o denominación del perceptor', sortable: true,
          sortKey: 'nombre', onCellClick: (row: Model193Recipient) => this.editar(row)
        },
        { key: 'clavePercepcion', label: 'Clave', sortable: true, sortKey: 'clavePercepcion', width: '110px', formatFn: (_: any, row: Model193Recipient) => (row.clavePercepcion + row.naturaleza) || '-' },
        { key: 'base', label: 'Base', sortable: true, sortKey: 'base', formatFn: dinero },
        { key: 'retencion', label: 'Retención', sortable: true, sortKey: 'retencion', formatFn: dinero }
      ],
      actions: [
        { label: 'Editar', icon: 'edit', callback: (row: Model193Recipient) => this.editar(row) },
        {
          label: 'Eliminar', icon: 'delete', callback: (row: Model193Recipient) => this.eliminar(row),
          visible: (row: Model193Recipient) => row.origen === ORIGEN_MANUAL
        }
      ],
      headerActions: [
        { label: 'Nuevo perceptor', icon: 'add', type: 'button', callback: () => this.nuevo() }
      ],
      filterView: 'model193Recipients',
      defaultFilter: {},
      selectable: false,
      pagination: false,
      noDataText: 'No hay perceptores en este ejercicio'
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$)
      )
      .subscribe(state => {
        this.lastState = state;
        this.cargar();
      });

    this.cdr.detectChanges();

    if (this.foco) {
      this.aplicarFoco();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.t1) return;

    if ((changes['version'] && !changes['version'].firstChange) ||
      (changes['ejercicio'] && !changes['ejercicio'].firstChange)) {
      this.recargar();
    }

    if (changes['foco'] && this.foco) {
      this.aplicarFoco();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private aplicarFoco(): void {
    if (!this.foco || this.foco.id === this.focoAplicado) return;

    this.focoAplicado = this.foco.id;
    this.quickSearch = this.foco.texto;
    this.abrirAlCargar = true;
    this.t1.tableState.patchFilters({ search: this.foco.texto });
  }


  onSearchChange(valor: string): void {
    this.quickSearch = valor || '';
    this.quickSearch$.next(this.quickSearch);
  }

  nuevo(): void {
    this.abrir(null);
  }

  editar(row: Model193Recipient): void {
    this.abrir(row);
  }

  eliminar(row: Model193Recipient): void {
    if (!row.uid) return;

    this.confirm.open({
      title: 'Eliminar perceptor',
      subtitle: row.nombre || row.nifPerceptor || undefined,
      message: row.sobrescribeClave
        ? '¿Seguro que quieres eliminar este perceptor? Volverá a aparecer el calculado desde tus facturas.'
        : '¿Seguro que quieres eliminar este perceptor del modelo 193?',
      tone: 'danger',
      details: [
        { label: 'NIF', value: row.nifPerceptor || '-' },
        { label: 'Clave', value: (row.clavePercepcion + row.naturaleza) || '-' }
      ],
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando perceptor...',
      errorText: 'No se pudo eliminar el perceptor',
      action: () => this.service.deleteRecipient(row.uid)
    }).subscribe(eliminado => {
      if (!eliminado) return;

      this.notify.ok('Perceptor eliminado');
      this.recargar();
      this.changed.emit();
    });
  }

  private abrir(row: Model193Recipient | null): void {
    const mensajes = row ? this.validations.filter(item => item.key === 'perceptor_' + row.clave) : [];

    Model193RecipientDialogComponent.open(this.dialog, {
      naturalezaDeclarante: !!this.declarante?.naturalezaDeclarante,
      row,
      ejercicio: this.ejercicio,
      validaciones: mensajes
    }).subscribe(cambiado => {
      if (!cambiado) return;

      this.recargar();
      this.changed.emit();
    });
  }

  private recargar(): void {
    if (this.t1.tableState.snapshot.pageIndex !== 0) {
      this.t1.tableState.setPageIndex(0);
      return;
    }

    this.cargar();
  }

  private async cargar(): Promise<void> {
    if (!this.t1) return;

    const turno = ++this.secuencia;
    const estado = this.lastState;
    const filtros = estado.filters || {};
    const tamano = estado.pageSize > 0 ? Math.min(estado.pageSize, 200) : 200;

    this.t1.tableState.setLoading(true);

    try {
      const respuesta = await this.service.getRecipients({
        year: this.ejercicio,
        page: estado.pageIndex + 1,
        pageSize: tamano,
        search: filtros.search || '',
        sortBy: estado.sortField || '',
        sortDirection: estado.sortDirection || ''
      });

      if (turno !== this.secuencia) return;

      this.t1.tableState.setData(respuesta.items);
      this.t1.tableState.setTotalItems(respuesta.total);
      this.totalPerceptores = respuesta.total;
      this.totalBase = respuesta.totalBase;
      this.totalRetenciones = respuesta.totalRetenciones;

      if (this.abrirAlCargar) {
        this.abrirAlCargar = false;

        if (respuesta.items.length === 1) {
          this.editar(respuesta.items[0]);
        }
      }
    } catch {
      if (turno === this.secuencia) {
        this.notify.error('No se pudieron cargar los perceptores');
      }
    } finally {
      if (turno === this.secuencia) {
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      }
    }
  }

  esContabilidad(row: Model193Recipient): boolean {
    return row.origen === ORIGEN_CONTABILIDAD;
  }
}
